import { Button, Divider, List, ListItemButton, ListItemText, Popover, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import type { ObserverAssignableUserInfo, ObserverIssueInfo } from "../../../api/observerTypes";

interface Props {
  anchorEl: HTMLElement;
  issue: ObserverIssueInfo;
  assignableUsers?: ObserverAssignableUserInfo[];
  currentUser?: ObserverAssignableUserInfo;
  onAssign: (issue: ObserverIssueInfo, assignedToUserId?: string) => void;
  onClose: () => void;
}

const ObserverAssignPopover = ({ anchorEl, issue, assignableUsers, currentUser, onAssign, onClose }: Props) => {
  const [search, setSearch] = useState("");

  const filteredUsers = useMemo(() => {
    const users = assignableUsers ?? [];
    const term = search.trim().toLowerCase();
    return term
      ? users.filter((user) => user.name.toLowerCase().includes(term) || user.email.toLowerCase().includes(term))
      : users;
  }, [assignableUsers, search]);

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Stack spacing={1} sx={{ width: 360, maxHeight: 480, p: 1.5 }}>
        <Stack spacing={1} sx={{ alignItems: "flex-start", pb: 1 }}>
          <Stack direction="row" spacing={1}>
            {currentUser && (
              <Button
                disabled={issue.assignedToUserId === currentUser.userId}
                onClick={() => onAssign(issue, currentUser.userId)}
              >
                Assign to me
              </Button>
            )}
            {issue.assignedToUserId && issue.status !== "Acknowledged" && (
              <Button onClick={() => onAssign(issue, undefined)}>Unassign</Button>
            )}
          </Stack>
          <SearchField fullWidth autoFocus onSearch={setSearch} />
        </Stack>

        <Divider />

        {filteredUsers.length > 0 ? (
          <List sx={{ overflowY: "auto", py: 0 }}>
            {filteredUsers.map((user) => (
              <ListItemButton
                key={user.userId}
                selected={user.userId === issue.assignedToUserId}
                onClick={() => onAssign(issue, user.userId)}
              >
                <ListItemText
                  primary={
                    user.email ? (
                      <Typography component="span">
                        {user.name}
                        <Typography component="span" color="textSecondary" sx={{ ml: 1 }}>
                          {user.email}
                        </Typography>
                      </Typography>
                    ) : (
                      user.name
                    )
                  }
                  slotProps={{ primary: { noWrap: true } }}
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
            No users found
          </Typography>
        )}
      </Stack>
    </Popover>
  );
};

export default ObserverAssignPopover;
